import type { BlockKind, EditorCitation } from './types';

/**
 * The text form of the E24 block kinds (ADR-026 decision 6; E24-design
 * §6; report-contract v2), so a consultant edits them in the same text box
 * as a paragraph and the PDF reads them the same way
 * (`packages/pdf-render/src/vendor-report-v2.ts` mirrors these readers):
 *
 *   - a `vendor_card` is three lines: the rank label, the body, and the
 *     Watch line (a leading "Watch:" dropped). Missing lines read as empty,
 *     so a card a consultant shortened still prints;
 *   - `scenarios` is one step per line, numbered by the renderer. A step's
 *     RTM column is the requirement citations the line carries (a
 *     citation's `lines`), printed by their label; its other citations
 *     print as record ids.
 */

export const BLOCK_KIND_LABEL: Record<BlockKind, string> = {
  paragraph: 'Paragraph',
  bullets: 'List',
  headline: 'Headline',
  vendor_card: 'Vendor card',
  scenarios: 'Scenarios',
};

/** What the edit box says under a block of each kind. */
export const EDIT_HINT: Record<BlockKind, string> = {
  paragraph: '',
  bullets: 'One item per line. ',
  headline: 'One sentence. ',
  vendor_card: 'Three lines: the rank label, the body, then "Watch: " and one sentence. ',
  scenarios: 'One step per line. The RTM numbers come from each step’s citations. ',
};

export interface VendorCardParts {
  rankLabel: string;
  body: string;
  watch: string;
}

export function vendorCardParts(text: string): VendorCardParts {
  const lines = text
    .split('\n')
    .map((l) => l.trim())
    .filter((l) => l.length > 0);
  return {
    rankLabel: lines[0] ?? '',
    body: lines[1] ?? '',
    watch: (lines[2] ?? '').replace(/^watch:\s*/i, ''),
  };
}

/** A list block's items: one per non-empty line, a leading "-", "*", "•" or a number dropped. */
export function listItems(text: string): string[] {
  return text
    .split('\n')
    .map((line) => line.replace(/^\s*(?:[-*•]\s+|\d+[.)]\s+)?/, '').trim())
    .filter((line) => line.length > 0);
}

export interface ScenarioStep {
  text: string;
  /** The requirement citations the line carries, as numbers ("39"). */
  rtm: string[];
  /** The line's other citations. */
  cites: EditorCitation[];
}

/** The citations that support line `n` (1-based) of a list block. */
export function citationsOnLine(
  citations: ReadonlyArray<EditorCitation>,
  n: number,
): EditorCitation[] {
  return citations.filter((c) => c.lines !== undefined && c.lines.includes(n));
}

export function scenarioSteps(
  text: string,
  citations: ReadonlyArray<EditorCitation> = [],
): ScenarioStep[] {
  return listItems(text).map((line, i) => {
    const onLine = citationsOnLine(citations, i + 1);
    return {
      text: line,
      // The column is headed RTM, so each reference prints as its number.
      rtm: onLine
        .filter((c) => c.kind === 'engagement_requirement')
        .map((c) => c.label.replace(/^RTM\s+/i, '')),
      cites: onLine.filter((c) => c.kind !== 'engagement_requirement'),
    };
  });
}
